import React, { useState } from 'react';

interface ImageOptimizedProps {
  src: string;
  alt: string;
  className?: string;
  width?: number;
  height?: number;
  priority?: boolean;
  placeholder?: string;
  category?: 'hero' | 'office' | 'thumbnail';
  responsive?: boolean;
}

export const ImageOptimized: React.FC<ImageOptimizedProps> = ({
  src,
  alt,
  className = '',
  width,
  height,
  priority = false,
  placeholder = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+PC9zdmc+',
  category = 'office',
  responsive = true
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  // Generate local optimized sources with responsive support
  const getLocalOptimizedSources = (originalSrc: string) => {
    const sources = [];
    
    // Check if it's a local optimized image path
    if (originalSrc.startsWith('/images/optimized/') || originalSrc.startsWith('./images/optimized/')) {
      const basePath = originalSrc.replace(/\.(avif|webp|jpe?g|png)$/i, '');
      
      if (responsive) {
        // Generate responsive sources for different screen sizes
        const sizes = ['mobile', 'tablet', 'desktop'];
        const formats = ['avif', 'webp', 'jpg'];
        
        sizes.forEach(size => {
          formats.forEach(format => {
            const srcPath = `${basePath}-${size}.${format}`;
            const mediaQuery = size === 'mobile' ? '(max-width: 768px)' : 
                             size === 'tablet' ? '(max-width: 1024px)' : 
                             '(min-width: 1025px)';
            
            sources.push({
              src: srcPath,
              type: `image/${format === 'jpg' ? 'jpeg' : format}`,
              media: mediaQuery,
              size: size
            });
          });
        });
      } else {
        // Single size, multiple formats
        const formats = ['avif', 'webp', 'jpg'];
        formats.forEach(format => {
          sources.push({
            src: `${basePath}.${format}`,
            type: `image/${format === 'jpg' ? 'jpeg' : format}`,
            size: 'default'
          });
        });
      }
    } else if (originalSrc.includes('unsplash.com')) {
      // Fallback for Unsplash images (existing logic)
      const baseUrl = new URL(originalSrc);
      if (width) baseUrl.searchParams.set('w', width.toString());
      if (height) baseUrl.searchParams.set('h', height.toString());
      
      // AVIF (best compression, newest format)
      const avifUrl = new URL(baseUrl);
      avifUrl.searchParams.set('fm', 'avif');
      avifUrl.searchParams.set('q', '75');
      sources.push({ src: avifUrl.toString(), type: 'image/avif', size: 'default' });
      
      // WebP (good compression, wide support)
      const webpUrl = new URL(baseUrl);
      webpUrl.searchParams.set('fm', 'webp');
      webpUrl.searchParams.set('q', '80');
      sources.push({ src: webpUrl.toString(), type: 'image/webp', size: 'default' });
      
      // Original format (fallback)
      sources.push({ src: originalSrc, type: 'image/jpeg', size: 'default' });
    } else {
      // For other images, just use original
      sources.push({ src: originalSrc, type: 'image/jpeg', size: 'default' });
    }
    
    return sources;
  };

  const sources = getLocalOptimizedSources(src);

  const handleLoad = () => {
    setIsLoaded(true);
  };

  const handleError = () => {
    setHasError(true);
    setIsLoaded(true);
  };

  // Generate sizes attribute for responsive images
  const getSizesAttribute = () => {
    if (!responsive) return undefined;
    
    if (category === 'hero') {
      return '(max-width: 768px) 768px, (max-width: 1024px) 1024px, 1920px';
    } else if (category === 'office') {
      return '(max-width: 768px) 400px, (max-width: 1024px) 600px, 800px';
    }
    return '(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw';
  };

  return (
    <div className={`relative overflow-hidden ${className}`}>
      {/* Placeholder */}
      {!isLoaded && (
        <img
          src={placeholder}
          alt=""
          className="absolute inset-0 w-full h-full object-cover blur-sm"
          aria-hidden="true"
        />
      )}
      
      {/* Progressive Enhancement with Picture Element */}
      <picture className="w-full h-full">
        {/* Modern formats with responsive support */}
        {responsive ? (
          // Responsive sources grouped by media queries
          <>
            {/* Desktop sources */}
            {sources.filter(s => s.size === 'desktop').map((source) => (
              <source
                key={`desktop-${source.type}`}
                srcSet={source.src}
                type={source.type}
                media="(min-width: 1025px)"
              />
            ))}
            
            {/* Tablet sources */}
            {sources.filter(s => s.size === 'tablet').map((source) => (
              <source
                key={`tablet-${source.type}`}
                srcSet={source.src}
                type={source.type}
                media="(max-width: 1024px)"
              />
            ))}
            
            {/* Mobile sources */}
            {sources.filter(s => s.size === 'mobile').map((source) => (
              <source
                key={`mobile-${source.type}`}
                srcSet={source.src}
                type={source.type}
                media="(max-width: 768px)"
              />
            ))}
          </>
        ) : (
          // Non-responsive sources
          sources.map((source, index) => (
            index < sources.length - 1 ? (
              <source
                key={source.type}
                srcSet={source.src}
                type={source.type}
              />
            ) : null
          ))
        )}
        
        {/* Fallback img element */}
        <img
          src={hasError ? src : sources[sources.length - 1]?.src || src}
          alt={alt}
          className={`w-full h-full object-cover transition-opacity duration-300 ${
            isLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          loading={priority ? 'eager' : 'lazy'}
          width={width}
          height={height}
          sizes={getSizesAttribute()}
          onLoad={handleLoad}
          onError={handleError}
          decoding="async"
        />
      </picture>
      
      {/* Loading indicator */}
      {!isLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      )}
    </div>
  );
};

export default ImageOptimized;